import React, { useState, useMemo } from 'react';
import { VariationNode } from '@/types/variationChain.types';
import { Star, GitBranch, Check, X, Play } from 'lucide-react';
import { Badge, Button } from '../ui';

interface VariationCardProps {
  node: VariationNode;
  onRate: (nodeId: string, rating: number) => void;
  onEvolve: (nodeId: string) => void;
  isRoot?: boolean;
}

const statusConfig: Record<string, { label: string; variant: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', variant: 'default-muted', icon: null },
  generating: { label: 'Generating', variant: 'warning-muted', icon: <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" /> },
  complete: { label: 'Complete', variant: 'success-muted', icon: <Check size={10} /> },
  failed: { label: 'Failed', variant: 'danger-muted', icon: <X size={10} /> },
};

function parseTweak(raw: any): { strategy?: string; continueClipId?: string; weirdness?: number; styleWeight?: number } | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

export const VariationCard: React.FC<VariationCardProps> = ({ node, onRate, onEvolve, isRoot }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const config = statusConfig[node.status] || statusConfig.pending;
  const tweak = useMemo(() => parseTweak(node.configTweak), [node.configTweak]);

  return (
    <div className={`bg-slate-900/60 border rounded-2xl p-4 transition-all ${
      isRoot ? 'border-accent/30' : 'border-slate-800 hover:border-slate-700'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-xs font-black text-slate-200 uppercase tracking-tight truncate">
              {node.title || `Gen ${node.generation}`}
            </h4>
            <Badge
              variant={config.variant as any}
              size="xs"
              leftIcon={config.icon}
              className="!text-[11px] flex-shrink-0"
            >
              {config.label}
            </Badge>
            {isRoot && (
              <Badge variant="info-muted" size="xs" className="!text-[11px]">Root</Badge>
            )}
          </div>
          <span className="text-[11px] text-slate-400 font-mono">Gen {node.generation}</span>
        </div>
      </div>

      {/* Audio Player */}
      {node.audioUrl && node.status === 'complete' && (
        <div className="mt-3">
          <audio
            controls
            preload="none"
            className="w-full h-8 [&::-webkit-media-controls-panel]:bg-slate-800 [&::-webkit-media-controls-panel]:rounded-lg"
          >
            <source src={node.audioUrl} type="audio/mpeg" />
          </audio>
        </div>
      )}

      {/* Generating animation */}
      {node.status === 'generating' && (
        <div className="mt-3 flex items-center gap-2 p-2 bg-amber-950/10 border border-amber-900/20 rounded-xl">
          <div className="w-3 h-3 border-2 border-slate-700 border-t-amber-400 rounded-full animate-spin" />
          <span className="text-[11px] text-amber-400 font-bold uppercase tracking-wider">Generating on Suno...</span>
        </div>
      )}

      {/* Rating + Evolve */}
      {node.status === 'complete' && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => onRate(node.id, star)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  size={14}
                  className={`transition-colors ${
                    star <= (hoverRating || node.overallRating || 0)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-slate-700'
                  }`}
                />
              </button>
            ))}
            {node.overallRating && (
              <span className="text-[11px] text-slate-400 font-mono ml-1">{node.overallRating}/5</span>
            )}
          </div>

          {(node.overallRating || 0) >= 3 && (
            <Button
              variant="primary"
              size="xs"
              onClick={() => onEvolve(node.id)}
              leftIcon={<GitBranch size={10} />}
              className="!text-[11px] !font-black !uppercase"
              title="Spawn new Suno generations from this node. Native keeps the same prompt and lets Suno create a different take. Tweak randomizes the weirdness & style weight sliders for creative variation. The prompt and tags stay identical — only the Suno control parameters change."
            >
              Evolve
            </Button>
          )}
        </div>
      )}

      {/* Changes from original */}
      {tweak && !isRoot && (
        <div className="mt-2 space-y-1.5">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Changes</span>
          <div className="flex flex-wrap gap-1.5">
            {tweak.continueClipId ? (
              <Badge variant="info-muted" size="xs" className="!text-[11px]">
                Native Variation (same prompt, different Suno take)
              </Badge>
            ) : tweak.strategy === 'native' ? (
              <Badge variant="info-muted" size="xs" className="!text-[11px]">
                Native (no parent clip to continue from)
              </Badge>
            ) : null}
            {tweak.strategy === 'tweak' || tweak.strategy === 'both' ? (
              <Badge variant="warning-muted" size="xs" className="!text-[11px]">Tweaked Parameters</Badge>
            ) : null}
            {tweak.weirdness !== undefined && (
              <Badge variant="default-muted" size="xs" className="!text-[11px]">
                Weirdness: {tweak.weirdness}
              </Badge>
            )}
            {tweak.styleWeight !== undefined && (
              <Badge variant="default-muted" size="xs" className="!text-[11px]">
                Style: {tweak.styleWeight}
              </Badge>
            )}
            {!tweak.weirdness && !tweak.styleWeight && !tweak.continueClipId && tweak.strategy !== 'tweak' && tweak.strategy !== 'both' && (
              <span className="text-[11px] text-slate-400 italic">Same prompt & tags — Suno generates a fresh interpretation</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
